import { Centered, Loading, MaterialIcon, MaterialIconName, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import React, { memo, useMemo } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { GptEventTimeLine } from '../../types/gpt';
import { getTimeBySeconds } from '../../utils/time';
import { EnhancedLabeledSelect } from '../ui/EnhancedLabeledSelect';
import { EVENT_ALARM, EVENT_ENGINE_OFF, EVENT_PATH } from '../../constants/gpt';
import { StyledTooltip } from '../ui/StyledTooltip';
import { useGptTimeLineDetail } from './hooks/useGptTimeLineDetail';

function GptTimeLineEvents() {
  const { theme, css } = useStyles();
  const { eventsTimeline, loadingEvent, eventTypeOptions, onEventTypeChange, eventTypeSelected, reloadEvent } =
    useGptTimeLineDetail();

  if (loadingEvent)
    return (
      <Centered>
        <Loading />
      </Centered>
    );

  if (!eventsTimeline) return null;

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
      })}
    >
      <div
        className={css({
          ...theme.typography.LabelLarge,
          width: '100%',
          alignItems: 'center',
          height: '50px',
          padding: '3px 10px',
          boxSizing: 'border-box',
          display: 'flex',
        })}
      >
        <div
          className={css({
            width: '100%',
          })}
        >
          {`Timeline Ultimas 13hrs. (Eventos: ${eventsTimeline.length})`}
        </div>
        <div
          className={css({
            minWidth: '200px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          })}
        >
          <EnhancedLabeledSelect
            options={eventTypeOptions}
            onChange={onEventTypeChange}
            value={[{ id: eventTypeSelected }]}
          />
          <StyledTooltip content="Recargar timeline" showArrow={false} placement="top" ignoreBoundary>
            <Button size="mini" shape="square" onClick={reloadEvent} kind="tertiary">
              <MaterialIcon name="autorenew" size="18px" />
            </Button>
          </StyledTooltip>
        </div>
      </div>
      <div
        className={css({
          width: '2px',
          borderLeft: '2px dotted',
          borderLeftColor: theme.colors.backgroundOverlayLight,
          position: 'absolute',
          left: '43px',
          top: '75px',
          height: 'calc(100% - 120px)',
        })}
      ></div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          position: 'relative',
          margin: '20px',
          gap: '40px',
          height: '100%',
          '::-webkit-scrollbar': {
            width: '8px',
          },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: theme.colors.backgroundOverlayDark,
            borderRadius: '4px',
          },
          '::-webkit-scrollbar-track': {
            background: theme.colors.backgroundSecondary,
            borderRadius: '4px',
          },
        })}
      >
        {eventsTimeline.map((event, i) => {
          return (
            <React.Fragment key={i}>
              {event.timelineType === EVENT_ALARM && <EventPoint event={event} index={i} />}
              {event.timelineType !== EVENT_ALARM && <PathEvent event={event} index={i} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const PathEvent = memo(({ event, index }: { event: GptEventTimeLine; index: number }) => {
  const { css, theme } = useStyles();
  const eventEvents = useMemo(() => {
    if (event.events?.length) return [...event.events].reverse();
    return undefined;
  }, [event.events]);

  if (!event.endTime) return null;

  return (
    <div
      className={css({
        width: '100%',
      })}
    >
      <div
        key={index}
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          position: 'relative',
          padding: '25px 10px 25px 22px',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: '#0000003A',
          },
        })}
        onMouseEnter={() => event.onHover && event.onHover(true)}
        onMouseLeave={() => event.onHover && event.onHover(false)}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '4px solid',
            borderLeftColor: theme.colors.backgroundOverlayLight,
            position: 'relative',
            gap: !!eventEvents ? 0 : '10px',
            width: '100%',
          })}
        >
          {/* First point */}
          <TimelinePoint
            backgroundColor={event.backgroundColor}
            iconName={event.iconName}
            label={event.endLabel}
            onClick={event.onClick}
            time={event.endTime}
          />

          {/* Events on the event path */}
          {!!eventEvents && (
            <ul
              className={css({
                paddingLeft: '1.2rem',
                paddingRight: 0,
                margin: 0,
                marginLeft: '45px',
                marginRight: '10px',
                listStyle: 'none',
                position: 'relative',
              })}
            >
              {eventEvents.map((e, i) => (
                <li key={`secondary-event-${i}`}>
                  <div
                    className={css({
                      display: 'flex',
                      alignItems: 'center',
                    })}
                  >
                    <div
                      className={css({
                        ...theme.typography.ParagraphXSmall,
                        flex: 1,
                      })}
                    >
                      {`${e.startLabel}`}
                    </div>
                    <StyledTooltip content="Encontrar en el mapa" showArrow={false} placement="top" ignoreBoundary>
                      <Button size="mini" shape="square" onClick={e.onClick} kind="tertiary">
                        <MaterialIcon name="location_searching" size="14px" />
                      </Button>
                    </StyledTooltip>
                    <div
                      className={css({
                        position: 'absolute',
                        left: 0,
                        transform: 'translateX(-50%)',
                        backgroundColor: e.backgroundColor ?? theme.colors.backgroundTertiary,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '22px',
                        height: '22px',
                        paddingBottom: '2px',
                        boxSizing: 'border-box',
                      })}
                    >
                      <MaterialIcon
                        name="warning"
                        size="14px"
                        color={e.backgroundColor ? theme.colors.contentInversePrimary : undefined}
                      />
                    </div>
                  </div>
                </li>
              ))}
              <li
                className={css({
                  ...theme.typography.ParagraphXSmall,
                  textAlign: 'center',
                  marginLeft: '-45px',
                })}
              >
                Velocidad promedio: {event.speed}km/h.
              </li>
            </ul>
          )}

          {/* Georeference of the shutdown start point */}
          {event.timelineType === EVENT_ENGINE_OFF && event.startReference && (
            <div
              className={css({
                ...theme.typography.ParagraphXSmall,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translateY(-50%) translateX(-50%)',
                textAlign: 'center',
              })}
            >
              {event.startReference}
            </div>
          )}

          {/* average speed of journeys */}
          {event.timelineType === EVENT_PATH && !eventEvents && (
            <div
              className={css({
                ...theme.typography.ParagraphXSmall,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translateY(-50%) translateX(-50%)',
                textAlign: 'center',
              })}
            >
              Velocidad promedio: {event.speed}km/h.
            </div>
          )}

          {/* Duration and distance data */}
          {(event.duration || event.duration === 0) && (
            <div
              className={css({
                ...theme.typography.ParagraphXSmall,
                fontSize: '11px',
                position: 'absolute',
                top: '50%',
                left: event.timelineType === EVENT_PATH ? '0px' : '-11px',
                transform: 'translateY(-50%) translateX(-50%) rotate(-90deg)',
                fontWeight: '600',
                letterSpacing: '0.05em',
                padding: '1px',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
              })}
            >
              <span>{getTimeBySeconds(event.duration, true)}</span>
              {event.timelineType === EVENT_PATH && <span>{event.distance}</span>}
            </div>
          )}

          {/* events */}
          {event.onClick && (
            <div
              className={css({
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                right: '10px',
              })}
            >
              <StyledTooltip content="Encontrar en el mapa" showArrow={false} placement="top" ignoreBoundary>
                <Button size="mini" shape="square" onClick={event.onClick} kind="tertiary">
                  <MaterialIcon name="location_searching" size="18px" />
                </Button>
              </StyledTooltip>
            </div>
          )}

          {/* Second point */}
          <TimelinePoint
            backgroundColor={event.backgroundColor}
            iconName={event.iconName}
            label={event.startLabel}
            onClick={event.onClick}
            time={event.startTime}
            atBottom
          />
        </div>
      </div>
    </div>
  );
});

const TimelinePoint = memo(
  ({
    backgroundColor,
    iconName,
    label,
    onClick,
    time,
    atBottom = false,
  }: {
    atBottom?: boolean;
    backgroundColor?: string;
    iconName: MaterialIconName;
    label?: string;
    onClick?: () => void;
    time: number;
  }) => {
    const { theme, css } = useStyles();
    const { formatDateFromTs, formatTimeFromTs } = useMoment();

    return (
      <div
        className={css({
          display: 'flex',
          minHeight: '30px',
          alignItems: 'center',
          width: '100%',
          position: 'relative',
          top: atBottom ? '16px' : '-16px',
        })}
      >
        <StyledTooltip
          content={formatDateFromTs(time, 'DD/MMMM/yyyy HH:mm:ss')}
          showArrow={false}
          placement="top"
          ignoreBoundary
        >
          <div
            className={css({
              ...theme.typography.HeadingXSmall,
              color: theme.colors.contentTertiary,
              width: '30px',
              height: '30px',
              backgroundColor: backgroundColor ?? theme.colors.backgroundTertiary,
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              left: '-17px',
            })}
          >
            <MaterialIcon name={iconName} size="15px" />
          </div>
        </StyledTooltip>
        <div
          className={css({
            ...theme.typography.LabelSmall,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          })}
        >
          <div
            className={css({
              width: '100%',
              display: 'flex',
            })}
          >
            {atBottom && time ? (label ? label : `${formatTimeFromTs(time)}`) : label}
          </div>
          {!atBottom && !time && onClick && (
            <div
              className={css({
                position: 'relative',
                right: '6px',
              })}
            >
              <StyledTooltip content="Encontrar en el mapa" showArrow={false} placement="top" ignoreBoundary>
                <Button size="mini" shape="square" onClick={onClick} kind="tertiary">
                  <MaterialIcon name="location_searching" size="18px" />
                </Button>
              </StyledTooltip>
            </div>
          )}
        </div>
      </div>
    );
  },
);

const EventPoint = memo(({ event, index }: { event: GptEventTimeLine; index: number }) => {
  const { css } = useStyles();

  return (
    <div
      className={css({
        width: '100%',
      })}
    >
      <div
        key={index}
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          position: 'relative',
          padding: '10px',
          paddingLeft: '25px',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: '#0000003A',
          },
        })}
        onMouseEnter={() => event.onHover && event.onHover(true)}
        onMouseLeave={() => event.onHover && event.onHover(false)}
      >
        <TimelinePoint
          backgroundColor={event.backgroundColor}
          iconName={event.iconName}
          label={event.startLabel}
          onClick={event.onClick}
          time={event.startTime}
        />
      </div>
    </div>
  );
});

export default GptTimeLineEvents;
